import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * One async read = one card. Every hub card owns its own loading / empty / error
 * state, so a single slow or broken endpoint degrades that card instead of
 * blanking the whole home screen.
 */
export interface Resource<T> {
  data: T | null;
  error: Error | null;
  loading: boolean;
  reload: () => void;
}

/**
 * @param key      changes to this string re-run the load (period, week, member…)
 * @param load     the fetch. Kept in a ref so an inline arrow doesn't loop.
 * @param enabled  false = never call (kids must not touch adult-only endpoints)
 */
export function useResource<T>(key: string, load: () => Promise<T>, enabled = true): Resource<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [nonce, setNonce] = useState(0);

  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    if (!enabled) {
      setData(null);
      setError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    loadRef.current().then(
      (result) => {
        if (cancelled) return;
        setData(result);
        setLoading(false);
      },
      (err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err : new Error(String(err)));
        setLoading(false);
      }
    );

    return () => {
      cancelled = true;
    };
  }, [key, enabled, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  return { data, error, loading, reload };
}
